// Lets the browser's or the OS's back close what lies over the screen (the
// Dock's menu) without changing the URL (it is not another page; the person
// asked for no page move, 2026-10-08).
//
// Promise: opening pushes one entry of the same URL, marked in its state;
// closing by its own means goes back over that entry (no extra history is
// left); going back closes it. Two opens push one entry. After a link inside
// it moved elsewhere, closing does not go back.
// The entry is not a page: a way back in history (the top Dock's) counts
// the pages before the screen under it, so opening a menu does not bring a
// back button (it did, 2026-10-18: "is opening a menu a page move?").
import type { RouterHistory } from "@tanstack/react-router";
import { useCallback, useSyncExternalStore } from "react";
import {
  hasPageBefore,
  isOverlayEntry,
  OVERLAY_MARK as MARK,
} from "../shell/page-history";

export { isOverlayEntry };

const KIND = "trameriaOverlayKind";

/**
 * On open: push the same URL once. `kind` names what was up, so coming back
 * to this entry (after a link inside moved elsewhere) can raise it again.
 */
export function pushOverlayEntry(history: RouterHistory, kind?: string): void {
  if (isOverlayEntry(history)) return;
  history.push(history.location.href, {
    [MARK]: true,
    [KIND]: kind ?? null,
  } as never);
}

/** What the current overlay entry was pushed for (`pushOverlayEntry`'s kind). */
export function overlayKind(history: RouterHistory): string | null {
  if (!isOverlayEntry(history)) return null;
  const state: unknown = history.location.state;
  const kind = (state as Record<string, unknown>)[KIND];
  return typeof kind === "string" ? kind : null;
}

/** On closing by its own means: go back over the pushed entry if still on it. */
export function leaveOverlayEntry(history: RouterHistory): void {
  if (isOverlayEntry(history)) history.back();
}

/**
 * Whether there is a page before the one shown, not counting an overlay's
 * entry, and the way back to it (over that entry too: the overlay closes).
 */
export function usePageBack(history: RouterHistory): {
  can: boolean;
  back(): void;
} {
  const can = useSyncExternalStore(history.subscribe, () =>
    hasPageBefore(history),
  );
  const back = useCallback(() => {
    if (isOverlayEntry(history)) history.go(-2);
    else history.back();
  }, [history]);
  return { can, back };
}
